import React, { useEffect, useRef } from 'react'
import { StateProvider, useStateValue } from '@State'
import { client } from '@Utils'
import { reportsEndpoints as endpoints } from '@URLS'
import { Alert, LoadingSpinner } from '@CommonComponents'
import { toast, Toaster } from '@CommonComponents/toast'

const FINISHED_STATUSES = ['finished', 'closed', 'cancelled']
const INPUT_TYPE_BY_ARG_TYPE = { integer: 'number', date: 'date' }
const AUDIT_FIELD_MAX_LENGTH = 500 // matches admin_reporting.AUDIT_FIELD_MAX_LENGTH

// Retries a request once, after a fresh WebAuthn touch, if it 403s with elevated_auth_required.
// Shared by run_script and task_result -- anywhere a request can hit an elevated-auth gate.
function withElevatedAuthRetry(requestFn) {
  return requestFn().catch(err => {
    if (err.response?.status === 403 && err.response?.data?.error === 'elevated_auth_required') {
      return window.elevatedAuth()
        .catch(() => { throw new Error('Security key verification is required.') })
        .then(requestFn)
    }
    throw err
  })
}

function elevatedAuthAwareMessage(err, fallback) {
  if (err.response?.status === 403 && err.response?.data?.error === 'elevated_auth_required') {
    return 'Security key verification failed. Please try again.'
  }
  return err.response?.data?.error || err.message || fallback
}

const initialState = {
  scripts: [],
  scriptsLoading: true,
  selectedSlug: null,
  formValues: {},
  audit: { recipient: '', reason: '' },
  activities: [],
  activitiesLoading: false,
  submitting: false,
  taskId: null,
  taskStatus: null,
  taskResult: null,
  taskResultLoading: false,
  taskResultError: null,
  error: null,
  recentTasks: [],
  recentTasksLoading: true,
  recentTasksRefreshKey: 0,
  recentTaskDownloads: {},
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SCRIPTS':
      return { ...state, scripts: action.scripts, scriptsLoading: false }
    case 'SET_SCRIPTS_ERROR':
      return { ...state, scriptsLoading: false, error: action.error }
    case 'SELECT_SCRIPT':
      return {
        ...state,
        selectedSlug: action.slug,
        formValues: {},
        audit: initialState.audit,
        activities: [],
        taskId: null,
        taskStatus: null,
        error: null,
      }
    case 'UPDATE_FIELD':
      return { ...state, formValues: { ...state.formValues, [action.name]: action.value } }
    case 'UPDATE_AUDIT_FIELD':
      return { ...state, audit: { ...state.audit, [action.name]: action.value } }
    case 'SET_ACTIVITIES_LOADING':
      return { ...state, activitiesLoading: action.loading }
    case 'SET_ACTIVITIES':
      // Also clears any previously-checked activity_pks for this field -- otherwise a stale
      // selection from a prior coursenum survives invisibly (the checkboxes reset because
      // `activities` is replaced, but formValues wasn't touched) and gets submitted as-is if
      // the user doesn't happen to re-toggle a checkbox before running the report.
      return {
        ...state,
        activities: action.activities,
        activitiesLoading: false,
        formValues: action.fieldName
          ? { ...state.formValues, [action.fieldName]: [] }
          : state.formValues,
      }
    case 'TOGGLE_ACTIVITY': {
      const updated = state.activities.map(a =>
        a.id === action.id ? { ...a, checked: !a.checked } : a
      )
      return {
        ...state,
        activities: updated,
        formValues: {
          ...state.formValues,
          [action.fieldName]: updated.filter(a => a.checked).map(a => a.id),
        },
      }
    }
    case 'BULK_SELECT_ACTIVITIES': {
      const updated = state.activities.map(a => ({ ...a, checked: action.checked }))
      return {
        ...state,
        activities: updated,
        formValues: {
          ...state.formValues,
          [action.fieldName]: action.checked ? updated.map(a => a.id) : [],
        },
      }
    }
    case 'SET_SUBMITTING':
      return { ...state, submitting: action.submitting }
    case 'SET_TASK':
      return {
        ...state,
        taskId: action.taskId,
        taskStatus: { status: 'new', progress: 0, description: 'Waiting to start...' },
        taskResult: null,
        taskResultLoading: false,
        taskResultError: null,
      }
    case 'SET_TASK_STATUS':
      return { ...state, taskStatus: action.taskStatus }
    case 'SET_ERROR':
      return { ...state, error: action.error }
    case 'RESET_TASK':
      return {
        ...state,
        taskId: null,
        taskStatus: null,
        taskResult: null,
        taskResultLoading: false,
        taskResultError: null,
        error: null,
        formValues: {},
        audit: initialState.audit,
        activities: [],
        selectedSlug: null,
        recentTasksLoading: true,
        recentTasksRefreshKey: state.recentTasksRefreshKey + 1,
      }
    case 'RESUME_TASK':
      return {
        ...state,
        selectedSlug: action.task.slug,
        taskId: action.task.task_id,
        taskStatus: {
          status: action.task.status,
          progress: action.task.progress,
          description: action.task.description,
          has_result: action.task.has_result,
          error: action.task.error,
        },
        taskResult: null,
        taskResultLoading: false,
        taskResultError: null,
        formValues: {},
        audit: initialState.audit,
        activities: [],
        error: null,
      }
    case 'SET_TASK_RESULT_LOADING':
      return { ...state, taskResultLoading: true, taskResultError: null }
    case 'SET_TASK_RESULT':
      return { ...state, taskResult: action.result, taskResultLoading: false }
    case 'SET_TASK_RESULT_ERROR':
      return { ...state, taskResultError: action.error, taskResultLoading: false }
    case 'SET_RECENT_TASKS':
      return { ...state, recentTasks: action.tasks, recentTasksLoading: false }
    case 'SET_RECENT_TASKS_ERROR':
      return { ...state, recentTasksLoading: false }
    case 'SET_RECENT_TASK_DOWNLOAD_LOADING':
      return {
        ...state,
        recentTaskDownloads: {
          ...state.recentTaskDownloads,
          [action.taskId]: { loading: true, result: null, error: null },
        },
      }
    case 'SET_RECENT_TASK_DOWNLOAD_RESULT':
      return {
        ...state,
        recentTaskDownloads: {
          ...state.recentTaskDownloads,
          [action.taskId]: { loading: false, result: action.result, error: null },
        },
      }
    case 'SET_RECENT_TASK_DOWNLOAD_ERROR':
      return {
        ...state,
        recentTaskDownloads: {
          ...state.recentTaskDownloads,
          [action.taskId]: { loading: false, result: null, error: action.error },
        },
      }
    default:
      return state
  }
}

export function App() {
  return (
    <StateProvider reducer={reducer} initialState={initialState}>
      <Toaster />
      <AppInner />
    </StateProvider>
  )
}

function AppInner() {
  const [state, dispatch] = useStateValue()
  const { scripts, scriptsLoading, selectedSlug, error, recentTasksRefreshKey } = state

  useEffect(() => {
    client
      .get(endpoints.listScripts())
      .then(res => dispatch({ type: 'SET_SCRIPTS', scripts: res.data.scripts }))
      .catch(() => dispatch({ type: 'SET_SCRIPTS_ERROR', error: 'Failed to load report scripts.' }))
  }, [])

  useEffect(() => {
    client
      .get(endpoints.recentTasks())
      .then(res => dispatch({ type: 'SET_RECENT_TASKS', tasks: res.data.tasks }))
      .catch(() => dispatch({ type: 'SET_RECENT_TASKS_ERROR' }))
  }, [recentTasksRefreshKey])

  const selectedScript = scripts.find(s => s.slug === selectedSlug)

  if (scriptsLoading) {
    return <LoadingSpinner text="Loading reports..." outerClass="text-center my-5" />
  }

  return (
    <div className="container-fluid mt-4">
      <h1 className="me-page-title">Reports</h1>

      {error && (
        <Alert bootstrapClass="danger" message={error} onDismiss={() => dispatch({ type: 'SET_ERROR', error: null })} />
      )}

      <div className="row">
        <div className="col-md-6">
          <ScriptList scripts={scripts} selectedSlug={selectedSlug} />
        </div>
        <div className="col-md-12">
          {selectedScript ? (
            <ScriptRunner script={selectedScript} />
          ) : (
            <p className="text-muted mt-2">Select a report from the list to get started.</p>
          )}
        </div>
        <div className="col-md-6">
          <RecentTasks />
        </div>
      </div>
    </div>
  )
}

function ScriptList({ scripts, selectedSlug }) {
  const [, dispatch] = useStateValue()

  return (
    <div className="list-group">
      {scripts.map(script => (
        <button
          key={script.slug}
          type="button"
          className={`list-group-item list-group-item-action${selectedSlug === script.slug ? ' active' : ''}`}
          onClick={() => dispatch({ type: 'SELECT_SCRIPT', slug: script.slug })}
        >
          <div className="font-weight-bold">{script.name}</div>
          <small className="color-me-darkgray">{script.description}</small>
        </button>
      ))}
    </div>
  )
}

function RecentTasks() {
  const [state, dispatch] = useStateValue()
  const { recentTasks, recentTasksLoading, recentTaskDownloads } = state

  const fetchDownloadLink = taskId => {
    dispatch({ type: 'SET_RECENT_TASK_DOWNLOAD_LOADING', taskId })
    withElevatedAuthRetry(() => client.post(endpoints.taskResult(taskId)))
      .then(res => dispatch({ type: 'SET_RECENT_TASK_DOWNLOAD_RESULT', taskId, result: res.data.result }))
      .catch(err => dispatch({
        type: 'SET_RECENT_TASK_DOWNLOAD_ERROR',
        taskId,
        error: elevatedAuthAwareMessage(err, 'Failed to get download link.'),
      }))
  }

  if (recentTasksLoading) {
    return <LoadingSpinner text="Loading recent reports..." outerClass="my-4" />
  }

  if (!recentTasks.length) {
    return (
      <div>
        <h4 className="mb-3">Recent Reports</h4>
        <p className="text-muted mt-2">No reports run yet.</p>
      </div>
    )
  }

  return (
    <div>
      <h4 className="mb-3">Recent Reports</h4>
      {recentTasks.map(task => {
        const isFinished = FINISHED_STATUSES.includes(task.status)
        const hasResults = isFinished && !task.error && task.has_result
        const hasError = isFinished && task.error
        const download = recentTaskDownloads[task.task_id]

        return (
          <div key={task.task_id} className="card-myed p-3 mb-3">
            <div>
              <strong>{task.script_name}</strong>
              <span className={`badge badge-${statusBadgeClass(task.status)} ml-2`}>
                {task.status}
              </span>
            </div>
            <small className="text-muted d-block mt-1">{new Date(task.created).toLocaleString()}</small>
            {!isFinished && (
              <button
                className="btn btn-myed btn-default btn-default-myed btn-sm-myed mt-2"
                onClick={() => dispatch({ type: 'RESUME_TASK', task })}
              >
                View Progress
              </button>
            )}
            {task.description && (
              <p className="text-muted small mb-2 mt-1">{task.description}</p>
            )}
            {hasError && (
              <Alert bootstrapClass="danger" message={`Report failed: ${task.error}`} />
            )}
            {hasResults && !download?.result && (
              <div className="mt-2">
                {download?.error && (
                  <Alert bootstrapClass="danger" message={download.error} />
                )}
                <button
                  type="button"
                  className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
                  disabled={download?.loading}
                  onClick={() => fetchDownloadLink(task.task_id)}
                >
                  {download?.loading ? 'Verifying security key...' : 'Get Download Link'}
                </button>
              </div>
            )}
            {download?.result && (
              <div className="mt-2">
                {download.result.map((url, i) => (
                  <div key={i} className="mb-1">
                    <a
                      href={url}
                      className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
                      download
                    >
                      <i className="fa-regular fa-download mr-2" aria-hidden="true" />
                      {new URL(url).pathname.split('/').pop()}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function statusBadgeClass(status) {
  switch (status) {
    case 'finished': return 'success'
    case 'processing': return 'primary'
    case 'new': return 'info'
    case 'cancelled': return 'secondary'
    default: return 'secondary'
  }
}

function ScriptRunner({ script }) {
  const [state, dispatch] = useStateValue()
  const {
    formValues, audit, activities, activitiesLoading, submitting, taskId, taskStatus,
    taskResult, taskResultLoading, taskResultError,
  } = state
  const pollingRef = useRef(null)

  const activityListArg = script.args.find(a => a.type === 'activity_list')

  useEffect(() => {
    if (!taskId) return
    if (taskStatus && FINISHED_STATUSES.includes(taskStatus.status)) return

    pollingRef.current = setInterval(() => {
      client
        .get(endpoints.taskStatus(taskId))
        .then(res => {
          dispatch({ type: 'SET_TASK_STATUS', taskStatus: res.data })
          if (FINISHED_STATUSES.includes(res.data.status)) {
            clearInterval(pollingRef.current)
          }
        })
        .catch(() => dispatch({ type: 'SET_ERROR', error: 'Failed to get task status.' }))
    }, 3000)

    return () => clearInterval(pollingRef.current)
  }, [taskId])

  const fetchActivities = coursenum => {
    if (!coursenum) return
    dispatch({ type: 'SET_ACTIVITIES_LOADING', loading: true })
    client
      .get(endpoints.activities(coursenum))
      .then(res =>
        dispatch({
          type: 'SET_ACTIVITIES',
          activities: res.data.activities.map(a => ({ id: String(a.pk), name: a.title, checked: false })),
          fieldName: activityListArg?.name,
        })
      )
      .catch(() => {
        dispatch({ type: 'SET_ACTIVITIES_LOADING', loading: false })
        dispatch({ type: 'SET_ERROR', error: `No course found for that course number.` })
      })
  }

  const submitScript = () => client.post(endpoints.runScript(), {
    slug: script.slug,
    args: formValues,
    recipient: audit.recipient,
    reason: audit.reason,
  })

  const handleSubmit = e => {
    e.preventDefault()
    dispatch({ type: 'SET_SUBMITTING', submitting: true })
    withElevatedAuthRetry(submitScript)
      .then(res => {
        dispatch({ type: 'SET_TASK', taskId: res.data.task_id })
      })
      .catch(err => toast.error(elevatedAuthAwareMessage(err, 'Failed to start report.')))
      .finally(() => dispatch({ type: 'SET_SUBMITTING', submitting: false }))
  }

  const fetchTaskResult = () => {
    dispatch({ type: 'SET_TASK_RESULT_LOADING' })
    withElevatedAuthRetry(() => client.post(endpoints.taskResult(taskId)))
      .then(res => dispatch({ type: 'SET_TASK_RESULT', result: res.data.result }))
      .catch(err => dispatch({
        type: 'SET_TASK_RESULT_ERROR',
        error: elevatedAuthAwareMessage(err, 'Failed to get download link.'),
      }))
  }

  const isFinished = taskStatus && FINISHED_STATUSES.includes(taskStatus.status)
  const hasError = isFinished && taskStatus.error
  const hasResults = isFinished && !taskStatus.error && taskStatus.has_result

  return (
    <div className="card-myed p-4">
      <h2 className="mb-1">{script.name}</h2>
      <p className="text-muted mb-4">{script.description}</p>

      {!taskId ? (
        <form onSubmit={handleSubmit}>
          {script.args.map(arg => (
            <ArgField
              key={arg.name}
              arg={arg}
              value={formValues[arg.name]}
              activities={activities}
              activitiesLoading={activitiesLoading}
              onFieldChange={(name, value) => dispatch({ type: 'UPDATE_FIELD', name, value })}
              onCoursenumBlur={fetchActivities}
              onActivityToggle={(id, fieldName) => dispatch({ type: 'TOGGLE_ACTIVITY', id, fieldName })}
              onBulkSelect={(checked, fieldName) => dispatch({ type: 'BULK_SELECT_ACTIVITIES', checked, fieldName })}
            />
          ))}

          <div className="form-group">
            <label htmlFor="audit_recipient">
              Who will receive this data?
              <span className="text-danger ml-1" aria-hidden="true">*</span>
            </label>
            <input
              id="audit_recipient"
              type="text"
              className="form-control"
              value={audit.recipient}
              onChange={e => dispatch({ type: 'UPDATE_AUDIT_FIELD', name: 'recipient', value: e.target.value })}
              maxLength={AUDIT_FIELD_MAX_LENGTH}
              required
              autoComplete="off"
            />
            <small className="form-text text-muted">The person or organization the export will be sent to.</small>
          </div>

          <div className="form-group">
            <label htmlFor="audit_reason">
              Reason
              <span className="text-danger ml-1" aria-hidden="true">*</span>
            </label>
            <textarea
              id="audit_reason"
              className="form-control"
              rows={3}
              value={audit.reason}
              onChange={e => dispatch({ type: 'UPDATE_AUDIT_FIELD', name: 'reason', value: e.target.value })}
              maxLength={AUDIT_FIELD_MAX_LENGTH}
              required
            />
            <small className="form-text text-muted">
              Why the data is needed. This is kept in the audit log, so do not include student names or other student details.
            </small>
          </div>

          <button
            type="submit"
            className="btn btn-myed btn-primary btn-primary-myed mt-2"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true" />
                Starting...
              </>
            ) : (
              'Run Report'
            )}
          </button>
        </form>
      ) : (
        <TaskProgress
          taskStatus={taskStatus}
          isFinished={isFinished}
          hasError={hasError}
          hasResults={hasResults}
          taskResult={taskResult}
          taskResultLoading={taskResultLoading}
          taskResultError={taskResultError}
          onFetchResult={fetchTaskResult}
          onReset={() => dispatch({ type: 'RESET_TASK' })}
        />
      )}
    </div>
  )
}

function ArgField({ arg, value, activities, activitiesLoading, onFieldChange, onCoursenumBlur, onActivityToggle, onBulkSelect }) {
  if (arg.type === 'coursenum') {
    return (
      <div className="form-group">
        <label htmlFor={arg.name}>
          {arg.label}
          {arg.required && <span className="text-danger ml-1" aria-hidden="true">*</span>}
        </label>
        <input
          id={arg.name}
          type="text"
          className="form-control"
          value={value || ''}
          onChange={e => onFieldChange(arg.name, e.target.value)}
          onBlur={e => onCoursenumBlur(e.target.value)}
          placeholder="e.g. CS101"
          autoComplete="off"
        />
        {arg.help_text && <small className="form-text text-muted">{arg.help_text}</small>}
      </div>
    )
  }

  if (arg.type === 'activity_list') {
    if (activitiesLoading) {
      return <LoadingSpinner text="Loading activities..." outerClass="my-3" />
    }
    if (!activities.length) {
      return (
        <div className="form-group">
          <label>{arg.label}</label>
          <p className="text-muted small mb-0">
            Enter a course number above to load activities.
          </p>
        </div>
      )
    }
    return (
      <div className="form-group">
        <ActivityList
          label={arg.label}
          activities={activities}
          onToggle={id => onActivityToggle(id, arg.name)}
          onSelectAll={checked => onBulkSelect(checked, arg.name)}
        />
        {arg.help_text && <small className="form-text text-muted mt-1 d-block">{arg.help_text}</small>}
      </div>
    )
  }

  return (
    <div className="form-group">
      <label htmlFor={arg.name}>
        {arg.label}
        {arg.required && <span className="text-danger ml-1" aria-hidden="true">*</span>}
      </label>
      <input
        id={arg.name}
        type={INPUT_TYPE_BY_ARG_TYPE[arg.type] || 'text'}
        className="form-control"
        value={value || ''}
        onChange={e => onFieldChange(arg.name, e.target.value)}
      />
      {arg.help_text && <small className="form-text text-muted">{arg.help_text}</small>}
    </div>
  )
}

function ActivityList({ label, activities, onToggle, onSelectAll }) {
  const allChecked = activities.length > 0 && activities.every(a => a.checked)

  return (
    <div>
      <label className="font-weight-bold d-block mb-1">{label}</label>
      <div className="border rounded">
        <label className="d-flex align-items-center px-3 py-2 mb-0 border-bottom pointer">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={e => onSelectAll(e.target.checked)}
          />
          <span className="ml-2 text-muted">Select all</span>
        </label>
        {activities.map(activity => (
          <label key={activity.id} className="d-flex align-items-center px-3 py-2 mb-0 pointer">
            <input
              type="checkbox"
              checked={activity.checked}
              value={activity.id}
              onChange={e => onToggle(e.target.value)}
            />
            <span className="ml-2">{activity.name}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function TaskProgress({
  taskStatus, isFinished, hasError, hasResults,
  taskResult, taskResultLoading, taskResultError, onFetchResult, onReset,
}) {
  if (!taskStatus) return null

  const pct = taskStatus.progress || 0
  const barClass = pct === 100 ? 'progress-bar progress-bar-success' : 'progress-bar'

  return (
    <div>
      <div className="progress">
        <div
          className={barClass}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin="0"
          aria-valuemax="100"
          style={{ width: `${pct}%` }}
        >
          {pct}%
        </div>
      </div>
      <p className="mt-2 text-muted">{taskStatus.description}</p>

      {!isFinished && (
        <p className="small text-muted">This may take a few minutes. You can leave this page and come back.</p>
      )}

      {hasError && (
        <Alert bootstrapClass="danger" message={`Report failed: ${taskStatus.error}`} />
      )}

      {hasResults && !taskResult && (
        <div className="mt-3">
          <p className="text-success mb-2">
            <i className="fa-regular fa-circle-check mr-2" aria-hidden="true" />
            <strong>Report complete!</strong> Verify your security key to get the download link.
          </p>
          {taskResultError && (
            <Alert bootstrapClass="danger" message={taskResultError} />
          )}
          <button
            type="button"
            className="btn btn-myed btn-primary btn-primary-myed"
            disabled={taskResultLoading}
            onClick={onFetchResult}
          >
            {taskResultLoading ? 'Verifying security key...' : 'Get Download Link'}
          </button>
        </div>
      )}

      {taskResult && (
        <div className="mt-3">
          <p className="text-success mb-2">
            <i className="fa-regular fa-circle-check mr-2" aria-hidden="true" />
            <strong>Report complete!</strong> Click to download:
          </p>
          {taskResult.map((url, i) => (
            <div key={i} className="mb-2">
              <a
                href={url}
                className="btn btn-myed btn-default btn-default-myed btn-sm-myed"
                download
              >
                <i className="fa-regular fa-download mr-2" aria-hidden="true" />
                {new URL(url).pathname.split('/').pop()}
              </a>
            </div>
          ))}
        </div>
      )}

      {isFinished && (
        <button
          type="button"
          className="btn btn-myed btn-link-myed mt-3"
          onClick={onReset}
        >
          Run another report
        </button>
      )}
    </div>
  )
}
